import axios from 'axios'
import moment from 'moment'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { some, partition, filter, isEmpty } from 'lodash'

import StadiumRemote from '../remote/stadiumRemote'

import useSafMode from '../hooks/useSafMode'

import { addPromotionsToOrder } from '../actions/promotion'
import {
  clearRemoteOrderTotal,
  fetchRemoteOrderTotalFailed,
  fetchRemoteOrderTotalSucceeded,
  fetchRemoteOrderTotalUpdate,
} from '../actions/orderTotalRemote'

import { getCartItems, getTaxExemptData } from '../selectors/cart'
import { getCurrentMenu, getIsKiosk, getUseTaxClasses } from '../selectors/menus'
import { getSelectedPromotions } from '../selectors/promotion'
import { getRemoteOrderTotal } from '../selectors/orderTotalRemote'
import { getOrderInProgress } from '../selectors/order'

import { getNetworkAvailableValue } from '../utils/networkConnected'
import { centsToDollars } from '../utils/formatters'
import { createOrderParams, makeLineItemData, parseModifiers } from '../utils/orderUtils'
import createDataDogLog from '../utils/datadog'
import { cartAsSinature, formatOrderTotals } from '../utils/orderTotalUtils'
import { ToastManager } from '../utils/toastManager'
import { currentOrderUuid } from '../utils/orderNumbers'

import { OFFLINE_SAF_MODE, ONLINE_SAF_MODE } from '../VNSyncManager/containers/VNSyncManager'

import { APPLICATION_STRINGS } from '../strings'
import { isNetworkError } from '../VNUtil/Errors'
import { setNetworkAvailable } from '../actions/config'
import { store } from '../stores'

export const TOTALS_REQUEST_TIMEOUT = 60_000 // 60 seconds
const FORCE_CONTINUE_IN_MS = 5_000
const TOTALS_HASH_THRESHOLD = 120 // seconds

export const formatItems = (items, pMakeLineItemData = true) => {
  let itemModels

  if (pMakeLineItemData) {
    itemModels = makeLineItemData(items) ?? []
  } else {
    itemModels = items
  }

  const orderMenuItems = itemModels.map((lineItem) => {
    return {
      menuItemUuid: lineItem?.menuItemUuid ?? lineItem?.id ?? '',
      quantity: lineItem.quantity,
      modifiers: parseModifiers(lineItem.modifiers, 1),
      price: centsToDollars(lineItem.priceInCents).slice(1),
      priceInCents: lineItem.priceInCents,
    }
  })

  return orderMenuItems
}

export const formatSplitItems = (items, pMakeLineItemData = true) => {
  let itemModels

  if (pMakeLineItemData) {
    itemModels = makeLineItemData(items) ?? []
  } else {
    itemModels = items
  }

  const orderMenuItems = itemModels.map((lineItem) => {
    return {
      menuItemUuid: lineItem?.lineItemUuid ?? '',
      quantity: lineItem.quantity,
      modifiers: parseModifiers(lineItem.modifiers, 1),
      price: centsToDollars(lineItem.priceInCents).slice(1),
      priceInCents: lineItem.priceInCents,
    }
  })

  return orderMenuItems
}

const useRemoteOrderTotal = () => {
  return useRemoteOrderTotalWithCallbacks(null, null, null)
}

export const useRemoteOrderTotalWithCallbacks = (
  cardReaderCallback,
  originalTotal,
  tipAmountInCents,
  donationAmountInCents = 0,
) => {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [failed, setFailed] = useState(false)
  const [data, setData] = useState()

  const orderInProgress = useSelector(getOrderInProgress)
  const menu = useSelector(getCurrentMenu)
  const menuUuid = createOrderParams(orderInProgress)?.standMenuUuid ?? menu?.uuid
  const taxExempt = useSelector(getTaxExemptData)?.taxExempt || false

  const [, updateSafMode] = useSafMode()

  let promotions = useSelector(getSelectedPromotions)
  const useTaxClasses = useSelector(getUseTaxClasses)

  let previousRemoteOrderTotal = useSelector(getRemoteOrderTotal)
  let manualPromotions = filter(promotions, (promotion) => promotion.redemptionMethod !== 'auto')

  if (isEmpty(manualPromotions)) {
    manualPromotions = undefined
  }

  const dispatch = useDispatch()
  const isKiosk = useSelector(getIsKiosk)

  const handleResult = (result, cartSignaturePayload, suppressToasts) => {
    const [applied, unApplied] = partition(result?.data?.promotions, (promotion) => promotion.applied || promotion.applies)

    if (!suppressToasts) {
      if (!isEmpty(unApplied) && some(unApplied, (promotion) => promotion.redemptionMethod === 'manual')) {
        ToastManager.error(APPLICATION_STRINGS.PROMOTIONS_NOT_APPLIED)
      } else {
        if (applied.some((promotion) => promotion.redemptionMethod === 'manual')) {
          ToastManager.success(APPLICATION_STRINGS.DISCOUNTS_APPLIED)
        }
      }
    }

    const hash = cartAsSinature({ ...cartSignaturePayload, promotions: applied })
    dispatch(addPromotionsToOrder({ selectedPromotions: applied }))
    dispatch(fetchRemoteOrderTotalSucceeded({ result, useTaxClasses, hash, taxExempt: orderInProgress?.taxExempt ?? taxExempt }))

    //TODO (JEFF) refactor the execution of formatting order totals in a way that makes sense across the code base
    setData(formatOrderTotals(result.data, useTaxClasses, orderInProgress?.taxExempt ?? taxExempt))
    setLoading(false)
    setSuccess(true)
  }

  const getGrandTotal = (totalInCents, tipAmountInCents, donationAmountInCents) => {
    return ((totalInCents + tipAmountInCents + donationAmountInCents) / 100).toFixed(2)
  }

  const resetOrderTotals = (isCancelled = false) => {
    setLoading(false)
    setSuccess(false)
    setFailed(false)
    setData({})
    dispatch(clearRemoteOrderTotal())
  }

  const executeRequest = (reTotaling, affiliations, userUuid = undefined, suppressToasts = false) => {
    let timeout = false
    const cartItems = getCartItems(store.getState())
    const orderMenuItems = formatItems(orderInProgress?.itemModels ?? cartItems, isEmpty(orderInProgress))

    if (createOrderParams(orderInProgress)?.promotions && reTotaling) {
      promotions = createOrderParams(orderInProgress)?.promotions
      manualPromotions = filter(promotions, (promotion) => promotion.redemptionMethod !== 'auto')

      if (isEmpty(manualPromotions)) {
        manualPromotions = undefined
      }
    }

    const cartSignaturePayload = cartAsSinature({
      orderTotal: originalTotal,
      uuid: currentOrderUuid(),
      items: orderMenuItems,
      promotions: manualPromotions,
      menuUuid,
      affiliations,
      taxExempt,
    })

    const curTotalHash = cartAsSinature(cartSignaturePayload)
    const isValidHash = moment().diff(moment(previousRemoteOrderTotal.updatedAt), 'seconds') <= TOTALS_HASH_THRESHOLD

    if (isValidHash && curTotalHash === previousRemoteOrderTotal.hash) {
      setData(previousRemoteOrderTotal)
      setSuccess(true)
      setLoading(false)

      if (cardReaderCallback) {
        cardReaderCallback(getGrandTotal(originalTotal, tipAmountInCents, donationAmountInCents))
      }
      return Promise.resolve()
    }

    setLoading(true)
    setSuccess(false)
    setFailed(false)

    const failureCardReaderCallback = () => {
      if (!cardReaderCallback) return
      cardReaderCallback(getGrandTotal(originalTotal, tipAmountInCents, donationAmountInCents))
    }

    const getTotalPromise = new Promise((resolve, reject) => {
      if (previousRemoteOrderTotal.hash && !reTotaling) {
        setData(previousRemoteOrderTotal)
        setLoading(false)
        setSuccess(true)
        resolve(previousRemoteOrderTotal)
        dispatch(fetchRemoteOrderTotalUpdate({ hash: curTotalHash }))

        return
      }
      const cancelToken = axios.CancelToken.source()
      const configOverride = {
        cancelToken: cancelToken?.token ?? '',
        timeout: TOTALS_REQUEST_TIMEOUT,
        checkTokenExpiration: true,
      }

      // This needs to be local because of closure weirdness.
      let totalsComplete = false

      //if offline or empty cart items, abort request and set status to fail
      if (!getNetworkAvailableValue() || isEmpty(orderMenuItems)) {
        setLoading(false)
        setSuccess(false)
        setFailed(true)

        return reject()
      }

      const timeoutCallback = () => {
        // If loading is still in progress, reject this promise.
        if (totalsComplete) return
        timeout = true
        setLoading(false)
        setSuccess(false)
        setFailed(true)

        if (cancelToken) cancelToken.cancel()

        // clearing previous total from state to prevent stale data
        dispatch(fetchRemoteOrderTotalFailed({}))

        // force offline mode to trigger offline calculations
        dispatch(setNetworkAvailable(false))

        failureCardReaderCallback()
        reject()
        createDataDogLog('error', '[Cart Totals] Remote Order Total Timed Out', {
          items: orderMenuItems,
          menuUuid,
          promotions,
          isKiosk,
        })
      }

      // If no manual promotions the timeout is around 5 seconds
      // With manual promotions the timeout is around 10 seconds.
      setTimeout(timeoutCallback, manualPromotions ? FORCE_CONTINUE_IN_MS * 2 : FORCE_CONTINUE_IN_MS)

      StadiumRemote.getRemoteOrderTotal(
        orderMenuItems,
        menuUuid,
        undefined,
        configOverride,
        isKiosk,
        affiliations,
        userUuid,
        orderInProgress?.taxExempt ?? taxExempt,
      )
        .then((result) => {
          const shouldForceOfflineMode = result?.data?.shouldForceOfflineMode

          if (shouldForceOfflineMode) {
            updateSafMode(OFFLINE_SAF_MODE, true)
          } else {
            updateSafMode(ONLINE_SAF_MODE, true)
          }

          if (!manualPromotions) {
            handleResult(result, cartSignaturePayload, suppressToasts)
            totalsComplete = true
            resolve(result.data)

            return
          }

          // TODO(mkramerl): This is problematic since the timer above can probably cancel this even
          // though this is a double-call and might take longer.
          StadiumRemote.getRemoteOrderTotal(
            orderMenuItems,
            menuUuid,
            [...manualPromotions, ...(result?.data?.promotions ?? [])],
            configOverride,
            isKiosk,
            affiliations,
            userUuid,
            orderInProgress?.taxExempt ?? taxExempt,
          )
            .then((result) => {
              handleResult(result, cartSignaturePayload, suppressToasts)
              totalsComplete = true
              resolve(result.data)
            })
            .catch((err) => {
              totalsComplete = true
              reject(err)
            })
        })
        .catch((err) => {
          if (isNetworkError(err)) {
            dispatch(setNetworkAvailable(false))
          }
          createDataDogLog('error', '[Cart Totals] Remote Order Total Failed', {
            err,
            items: orderMenuItems,
            menuUuid,
            promotions,
            configOverride,
            isKiosk,
          })

          dispatch(fetchRemoteOrderTotalFailed(err))

          setLoading(false)
          setSuccess(false)
          setFailed(true)

          totalsComplete = true
          reject(err)
        })
    })
      .then((data) => {
        if (!cardReaderCallback) return

        cardReaderCallback(getGrandTotal(data?.totalAmountInCents ?? originalTotal, tipAmountInCents, donationAmountInCents))
      })
      .catch((err) => {
        if (isNetworkError(err)) {
          dispatch(setNetworkAvailable(false))
        }
        if (timeout) return
        createDataDogLog('error', '[Cart Totals] Card Reader Callback failed in  Remote Order Total', {
          err,
          items: orderMenuItems,
          menuUuid,
          promotions,
          isKiosk,
        })
        failureCardReaderCallback()
      })

    return getTotalPromise
  }

  return [executeRequest, { data, loading, success, failed }, resetOrderTotals]
}

export default useRemoteOrderTotal
